import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import MainLayout from "@/components/layout/MainLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Loader2, ShieldAlert, Sparkles, GripVertical, Plus, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Succession Kit, Stage Three: The Decisions Interview.
 * One question per screen, autosaves to succession_profiles.decisions.
 */

type Decisions = Record<string, any>;
type BranchFlags = Record<string, boolean>;

type ScreenKind =
  | "welcome"
  | "teaching"
  | "radio"
  | "text"
  | "textarea"
  | "number"
  | "multiselect"
  | "ordered_list"
  | "confirm"
  | "complete";

interface Screen {
  key: string;
  kind: ScreenKind;
  section?: string;
  title: string;
  subtitle?: string;
  helper?: string;
  when?: (d: Decisions, flags: BranchFlags, answers: Record<string, any>) => boolean;
  // Grouped composite fields for radio + text follow-ups
  render?: React.FC<{ decisions: Decisions; setDecisions: (updater: (d: Decisions) => Decisions) => void }>;
}

async function resolveAdminUna(userId: string): Promise<{ id: string; name: string } | null> {
  const { data: unaAsTS } = await supabase
    .from("unas")
    .select("id, name")
    .eq("treasury_secretary_id", userId)
    .maybeSingle();
  if (unaAsTS) return { id: unaAsTS.id as string, name: (unaAsTS.name as string) || "" };
  const { data: member } = await supabase
    .from("una_members")
    .select("una_id, una:una_id(name)")
    .eq("user_id", userId)
    .eq("status", "active")
    .in("member_role", ["managing_member", "treasury_secretary"])
    .limit(1)
    .maybeSingle();
  if (!member?.una_id) return null;
  return { id: member.una_id as string, name: (member.una as any)?.name || "" };
}

/* ---------- Ordered list editor ---------- */
function OrderedList({
  value,
  onChange,
  placeholder,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");
  const list = Array.isArray(value) ? value : [];
  function add() {
    const v = draft.trim();
    if (!v) return;
    onChange([...list, v]);
    setDraft("");
  }
  function remove(i: number) {
    onChange(list.filter((_, idx) => idx !== i));
  }
  function move(i: number, dir: -1 | 1) {
    const next = list.slice();
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {list.map((name, i) => (
          <div key={i} className="flex items-center gap-2 border rounded-md p-3 bg-background">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm font-medium w-6">{i + 1}.</span>
            <span className="flex-1">{name}</span>
            <Button variant="ghost" size="sm" onClick={() => move(i, -1)} disabled={i === 0}>Up</Button>
            <Button variant="ghost" size="sm" onClick={() => move(i, 1)} disabled={i === list.length - 1}>Down</Button>
            <Button variant="ghost" size="icon" onClick={() => remove(i)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder || "Add a name"}
        />
        <Button onClick={add} variant="outline">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </div>
    </div>
  );
}

/* ---------- Multiselect ---------- */
function MultiSelect({
  value,
  onChange,
  options,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  options: { value: string; label: string }[];
}) {
  const set = new Set(value || []);
  function toggle(v: string) {
    const next = new Set(set);
    if (next.has(v)) next.delete(v);
    else next.add(v);
    onChange(Array.from(next));
  }
  return (
    <div className="space-y-2">
      {options.map((o) => {
        const checked = set.has(o.value);
        return (
          <label key={o.value} className="flex items-center gap-3 border rounded-md p-3 cursor-pointer hover:bg-accent">
            <Checkbox checked={checked} onCheckedChange={() => toggle(o.value)} />
            <span>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function SuccessionDecisions() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [unaId, setUnaId] = useState<string | null>(null);
  const [unaName, setUnaName] = useState<string>("");
  const [profileId, setProfileId] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [branchFlags, setBranchFlags] = useState<BranchFlags>({});
  const [decisions, setDecisions] = useState<Decisions>({});
  const [stepIdx, setStepIdx] = useState(0);
  const [hasOpenFlag, setHasOpenFlag] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    (async () => {
      if (!user?.id) return;
      const una = await resolveAdminUna(user.id);
      if (!una) {
        toast.error("Please complete your Association setup first.");
        navigate("/dashboard");
        return;
      }
      setUnaId(una.id);
      setUnaName(una.name);
      const { data: prof } = await supabase
        .from("succession_profiles")
        .select("*")
        .eq("association_id", una.id)
        .order("created_at", { ascending: false })
        .limit(1).maybeSingle();
      if (!prof) {
        toast.error("Please complete your Succession Profile first.");
        navigate("/succession");
        return;
      }
      setProfileId(prof.id);
      setAnswers((prof.answers as any) || {});
      setBranchFlags((prof.branch_flags as any) || {});
      const existingDecisions = (prof.decisions as any) || {};
      setDecisions(existingDecisions);

      const { data: openFlags } = await supabase
        .from("succession_review_flags")
        .select("id")
        .eq("profile_id", prof.id)
        .is("resolved_at", null);
      setHasOpenFlag((openFlags?.length ?? 0) > 0);

      // Resume by screen key
      const resumeKey = existingDecisions?._resume_key;
      if (resumeKey) {
        const built = buildScreens(existingDecisions, (prof.branch_flags as any) || {}, (prof.answers as any) || {});
        const idx = built.findIndex((s) => s.key === resumeKey);
        if (idx >= 0) setStepIdx(idx);
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const screens = useMemo(
    () => buildScreens(decisions, branchFlags, answers),
    [decisions, branchFlags, answers],
  );
  const current = screens[Math.min(stepIdx, screens.length - 1)];
  const progressPct = Math.round(((stepIdx + 1) / screens.length) * 100);

  // Autosave
  useEffect(() => {
    if (!profileId || loading) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      await supabase
        .from("succession_profiles")
        .update({ decisions: { ...decisions, _resume_key: current?.key || null } })
        .eq("id", profileId);
    }, 500);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decisions, stepIdx, profileId]);

  function setPath(path: string, value: any) {
    setDecisions((d) => {
      const next = { ...d };
      const keys = path.split(".");
      let cursor: any = next;
      for (let i = 0; i < keys.length - 1; i++) {
        cursor[keys[i]] = { ...(cursor[keys[i]] || {}) };
        cursor = cursor[keys[i]];
      }
      cursor[keys[keys.length - 1]] = value;
      return next;
    });
  }

  function getPath(path: string): any {
    const keys = path.split(".");
    let cursor: any = decisions;
    for (const k of keys) {
      if (cursor == null) return undefined;
      cursor = cursor[k];
    }
    return cursor;
  }

  function canAdvance(): boolean {
    if (!current) return false;
    if (["welcome", "teaching", "complete"].includes(current.kind)) return true;
    // Every real screen validates through per-screen render; keep permissive.
    return true;
  }

  async function goNext() {
    if (!current) return;
    if (current.kind === "complete") {
      navigate("/dashboard");
      return;
    }
    if (stepIdx === screens.length - 2) {
      setSaving(true);
      await supabase
        .from("succession_profiles")
        .update({
          decisions: { ...decisions, _complete: true, _resume_key: "complete" },
        })
        .eq("id", profileId!);
      setSaving(false);
    }
    setStepIdx((i) => Math.min(i + 1, screens.length - 1));
  }

  function goBack() {
    setStepIdx((i) => Math.max(0, i - 1));
  }

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </MainLayout>
    );
  }

  if (hasOpenFlag) {
    return (
      <MainLayout>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <Card className="p-8 border-amber-300 bg-amber-50 space-y-4">
            <div className="flex items-center gap-3">
              <ShieldAlert className="h-6 w-6 text-amber-700" />
              <h2 className="text-2xl font-serif">Paused for review</h2>
            </div>
            <p className="text-base text-amber-900">
              A note asking for review is on your Succession Profile. Decisions have to wait until our team clears it.
            </p>
            <p className="text-sm text-amber-800">
              We will be in touch. In the meantime, you can go back to your dashboard.
            </p>
            <Button variant="outline" onClick={() => navigate("/dashboard")}>Return to dashboard</Button>
          </Card>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-[80vh] flex flex-col">
        <div className="w-full max-w-3xl mx-auto px-4 pt-6">
          <Progress value={progressPct} className="h-2" />
          <div className="flex justify-between text-xs text-muted-foreground mt-2">
            <span>{current?.section || "Decisions"}</span>
            <span>{stepIdx + 1} of {screens.length}</span>
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center px-4 py-10">
          <div className="w-full max-w-2xl">
            <ScreenView
              screen={current}
              decisions={decisions}
              setDecisions={setDecisions}
              setPath={setPath}
              getPath={getPath}
              onBegin={goNext}
              unaName={unaName}
              answers={answers}
              branchFlags={branchFlags}
              navigate={navigate}
            />
          </div>
        </div>
        {current?.kind !== "welcome" && (
          <div className="w-full max-w-3xl mx-auto px-4 pb-10 flex items-center justify-between">
            <Button variant="ghost" onClick={goBack} disabled={stepIdx === 0}>
              <ArrowLeft className="h-4 w-4 mr-2" /> Back
            </Button>
            <Button size="lg" onClick={goNext} disabled={!canAdvance() || saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {current?.kind === "complete" ? "Return to dashboard" : "Continue"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </div>
    </MainLayout>
  );
}

/* ============================================================
 *  Screen definitions
 * ============================================================ */

function buildScreens(d: Decisions, flags: BranchFlags, answers: Record<string, any>): Screen[] {
  const screens: Screen[] = [
    { key: "welcome", kind: "welcome", title: "The choices that shape your plan",
      subtitle: "Your Constitution already lets succession be whatever your family needs. These questions write your choices down so no one has to guess later. Along the way we will use a few key words. A <em>resolution</em> is a short paper where the Board (the group of officers) writes down a choice. A <em>Board</em> is that group. To <em>adopt</em> a resolution just means to vote it in. When we say <em>notarize</em>, we mean sign in front of a notary, a person the state trusts to watch a signature." },

    // 2. The triggers
    { key: "triggers_teach", kind: "teaching", section: "The moments that turn things on",
      title: "The four moments",
      subtitle: "The role passes at four moments: death, incapacity (a season when an officer cannot serve because of illness or injury), the officer stepping down, or a Board vote turning the plan on. A moment set in calm times never turns into a fight in a hard time." },
    { key: "incapacity_who", kind: "radio", section: "The moments that turn things on", title: "Who decides that an officer is not able to serve?" },
    { key: "incapacity_wait", kind: "radio", section: "The moments that turn things on", title: "When does the plan turn on?" },

    // 3. Order of succession
    { key: "order_teach", kind: "teaching", section: "The order of succession",
      title: "The order of succession",
      subtitle: "Name the people who could take the Principal Executive Officer role, in the order you want them to serve. Then do the same for your Secretary Treasurer. If every name has been used, your Constitution lets any living Voting Member call an Emergency Assembly, so the Association is never stuck." },
    { key: "order_chief", kind: "ordered_list", section: "The order of succession",
      title: "Principal Executive Officer, in order",
      helper: "First, second, third. Move names up or down or take one out as you need." },
    { key: "order_secretary", kind: "ordered_list", section: "The order of succession",
      title: "Secretary Treasurer, in order",
      helper: "First, second, third. Move names up or down or take one out as you need." },
    { key: "order_gap", kind: "radio", section: "The order of succession",
      title: "If a named successor cannot or will not serve, what happens?" },

    // 4. Minor successor
    { key: "minor_teach", kind: "teaching", section: "The young successor",
      title: "The young successor",
      subtitle: "A young person signs nothing. Getting ready is learning, not a contract. The bridge successor holds the role and teaches until the young one is ready. The handoff, when it comes, happens by a Board vote, and we prepare that paper in the Documents stage.",
      when: (_d, f) => !!f.minor_successor },
    { key: "minor_age", kind: "number", section: "The young successor",
      title: "At what age is your young successor old enough to hold the role?",
      helper: "18 is the earliest. Some families choose 21 or 25. You know your family.",
      when: (_d, f) => !!f.minor_successor },
    { key: "minor_readiness", kind: "multiselect", section: "The young successor",
      title: "What else shows they are ready, besides age?",
      when: (_d, f) => !!f.minor_successor },

    // 5. Non-participating family
    { key: "np_teach", kind: "teaching", section: "Family who want to be cared for without leading",
      title: "Care without leading",
      subtitle: "Succession here passes a job, never a thing to keep. The Association is not an estate and its things are not your children's to inherit. A family member who does not want to lead has two honest paths. First, your own estate plan, written with a licensed helper, can care for them from your own things. Second, your Constitution lets the Board, by a full vote of every officer, set up a private trust the family member can be paid from, and that payment is taxable to them. The details and the tax side belong with a licensed helper. What we write here is only your wishes." },
    { key: "np_path", kind: "radio", section: "Family who want to be cared for without leading",
      title: "How do you plan to care for family members who do not want to lead?" },
    { key: "np_note", kind: "textarea", section: "Family who want to be cared for without leading",
      title: "Any wish you want on the record?",
      helper: "Please do not include passwords, PINs, or account numbers." },

    // 6. Steward Protector
    { key: "protector_teach", kind: "teaching", section: "The Steward Protector",
      title: "The Steward Protector, a role you may choose",
      subtitle: "Some families name a trusted person outside the officers to watch over the handoff, like a referee who only steps on the field when needed. The Steward Protector is quiet while officers serve. If you create this role, its powers are small: say when an officer cannot serve (under your written rule), turn on your plan, break a tie on the Board after a spot opens up, and call the Emergency Assembly. The Protector never holds things, never votes in day to day work, and never serves as a successor at the same time." },
    { key: "protector_choice", kind: "radio", section: "The Steward Protector",
      title: "Would you like a Steward Protector role in your Association?" },

    // 7. Notifications
    { key: "notif_when", kind: "radio", section: "Who is told, and when",
      title: "When should your successors know they have been named?" },
    { key: "notif_bank_teach", kind: "teaching", section: "Who is told, and when",
      title: "Banks and other places you deal with",
      subtitle: "Banks honor their own paperwork, not what we write inside the Association. So we set up two paths ahead of time. Where your bank allows it, your successor is added now as a signer in waiting (an <em>authorized signer</em> is a person the bank has on file to sign for the account). Everywhere else, we get the exact letter package that place will ask for on the day. You will build the list of these places in the Execution stage." },
    { key: "notif_who", kind: "multiselect", section: "Who is told, and when",
      title: "Who else should be told when the plan turns on?" },

    // 8. Asset-flag conditional screens
    { key: "asset_digital", kind: "confirm", section: "Online money and keys",
      title: "About online money and keys",
      subtitle: "Seed phrases (the secret words that back up a digital wallet) stay on paper and are never typed anywhere online, this portal included. The plan only writes down where things live, never the secrets. For bigger balances, a plan that uses more than one key, checked by a licensed helper, means one lost key is not the end.",
      when: (_d, f) => !!f.has_digital_assets },
    { key: "asset_business", kind: "confirm", section: "A business you run",
      title: "About your business",
      subtitle: "Your company's own written rules already let the manager job pass right on, and they let you keep a signed paper on file that says who will take over, so the company never stops for a day.",
      when: (_d, f) => !!f.has_operating_business },
    { key: "asset_real", kind: "confirm", section: "Real property (land or a home)",
      title: "About real property",
      subtitle: "Title stays with the Association or LLC (a limited liability company, the legal box that holds a business or property), so there is no probate court to go through. The work is telling the right people and keeping things going. Your insurance company and any property manager join the tell list.",
      when: (_d, f) => !!f.has_real_property },
    { key: "asset_brokerage", kind: "confirm", section: "Investment accounts",
      title: "About investment accounts",
      subtitle: "The company that holds your investments has its own succession forms. Each one joins your list in the Execution stage.",
      when: (_d, f) => !!f.has_brokerage },
    { key: "asset_trust", kind: "confirm", section: "A private trust",
      title: "About your private trust",
      subtitle: "Your trust has its own succession rules. This plan works alongside the trust and never overrides it. Look at both together with a licensed helper.",
      when: (_d, f) => !!f.has_private_trust },

    // 9. Standard
    { key: "standard_teach", kind: "teaching", section: "The way we like to do it, that your successor takes on",
      title: "The way we like to do it, that your successor takes on",
      subtitle: "The Covenant, the teaching, and the lines bind a successor the same way they bind you. Your successor already said yes to this in the Seven Promises. The power to use Association things always comes from the Constitution and its votes, never from a title on its own." },

    { key: "outlive_teach", kind: "teaching", section: "Why this lasts past you",
      title: "Why this lasts past you",
      subtitle: "The Association is meant to go on forever. It cannot be shut down while even one officer is willing to keep going. Shutting it down takes a full vote of every officer on the Board and a gift to like minded groups, under the Constitution's own rules. Succession is here so that day never has to come." },

    { key: "complete", kind: "complete",
      title: "Your choices are on the record.",
      subtitle: "The Documents stage will build your papers from these choices." },
  ];

  return screens.filter((s) => !s.when || s.when(d, flags, answers));
}

/* ---------- Screen renderer ---------- */
function ScreenView(props: {
  screen: Screen | undefined;
  decisions: Decisions;
  setDecisions: React.Dispatch<React.SetStateAction<Decisions>>;
  setPath: (p: string, v: any) => void;
  getPath: (p: string) => any;
  onBegin: () => void;
  unaName: string;
  answers: Record<string, any>;
  branchFlags: BranchFlags;
  navigate: (to: string) => void;
}) {
  const { screen, setPath, getPath, onBegin, answers, navigate } = props;
  if (!screen) return null;

  if (screen.kind === "welcome") {
    return (
      <div className="text-center space-y-8">
        <Sparkles className="h-10 w-10 mx-auto text-primary" />
        <h1 className="text-4xl md:text-5xl font-serif tracking-tight">{screen.title}</h1>
        <p className="text-xl text-muted-foreground max-w-xl mx-auto">{screen.subtitle}</p>
        <Button size="lg" className="text-lg px-10 py-6" onClick={onBegin}>Begin</Button>
      </div>
    );
  }

  if (screen.kind === "teaching") {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl md:text-4xl font-serif">{screen.title}</h2>
        <p className="text-lg text-muted-foreground leading-relaxed whitespace-pre-line">{screen.subtitle}</p>
      </div>
    );
  }

  if (screen.kind === "complete") {
    const items = summarizePrepared(props.decisions, props.branchFlags);
    return (
      <div className="space-y-6">
        <h2 className="text-3xl md:text-4xl font-serif">{screen.title}</h2>
        <p className="text-lg text-muted-foreground">{screen.subtitle}</p>
        {items.length > 0 && (
          <Card className="p-6 space-y-3">
            <p className="text-sm font-medium">The Documents stage will get these ready:</p>
            <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
              {items.map((i) => <li key={i}>{i}</li>)}
            </ul>
          </Card>
        )}
      </div>
    );
  }

  // Custom per-key renders
  switch (screen.key) {
    case "incapacity_who": {
      const v = getPath("incapacity.who") || "";
      const name = getPath("incapacity.trusted_name") || "";
      const other = getPath("incapacity.who_other") || "";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("incapacity.who", val)} className="space-y-3">
            {[
              { value: "two_physicians", label: "Two licensed doctors, in writing" },
              { value: "officers_resolution", label: "The other officers, by a full vote" },
              { value: "trusted_plus_physician", label: "A trusted person I name, plus one doctor" },
              { value: "other", label: "Something else" },
            ].map((o) => (
              <OptionRow key={o.value} name={`incap-${o.value}`} value={o.value} label={o.label} groupValue={v} />
            ))}
          </RadioGroup>
          {v === "trusted_plus_physician" && (
            <Input className="mt-3" placeholder="Name of the trusted person" value={name} onChange={(e) => setPath("incapacity.trusted_name", e.target.value)} />
          )}
          {v === "other" && (
            <Textarea className="mt-3" placeholder="Say your rule in your own words" value={other} onChange={(e) => setPath("incapacity.who_other", e.target.value)} />
          )}
        </Panel>
      );
    }
    case "incapacity_wait": {
      const v = getPath("incapacity.wait") || "";
      const other = getPath("incapacity.wait_other") || "";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("incapacity.wait", val)} className="space-y-3">
            {[
              { value: "thirty_days", label: "After 30 days out of reach, once we have really tried" },
              { value: "immediate", label: "Right away, once it is written down" },
              { value: "other", label: "Something else" },
            ].map((o) => (
              <OptionRow key={o.value} name={`wait-${o.value}`} value={o.value} label={o.label} groupValue={v} />
            ))}
          </RadioGroup>
          {v === "other" && (
            <Textarea className="mt-3" placeholder="Say your rule in your own words" value={other} onChange={(e) => setPath("incapacity.wait_other", e.target.value)} />
          )}
          <p className="text-sm text-muted-foreground mt-4">A moment set in calm times never turns into a fight in a hard time.</p>
        </Panel>
      );
    }
    case "order_chief": {
      const list: string[] = getPath("order.chief") || (answers.successor_for_chief ? [answers.successor_for_chief] : []);
      return (
        <Panel screen={screen}>
          <OrderedList value={list} onChange={(v) => setPath("order.chief", v)} placeholder="e.g. Jane Doe" />
        </Panel>
      );
    }
    case "order_secretary": {
      const list: string[] = getPath("order.secretary") || (answers.successor_for_secretary ? [answers.successor_for_secretary] : []);
      return (
        <Panel screen={screen}>
          <OrderedList value={list} onChange={(v) => setPath("order.secretary", v)} placeholder="e.g. John Doe" />
        </Panel>
      );
    }
    case "order_gap": {
      const v = getPath("order.gap") || "next_in_order";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("order.gap", val)} className="space-y-3">
            <OptionRow name="gap-next" value="next_in_order" label="The next name in the list steps up" groupValue={v} />
            <OptionRow name="gap-board" value="board_resolves" label="The Board decides at that time, by vote" groupValue={v} />
          </RadioGroup>
          <p className="text-sm text-muted-foreground mt-4">
            If every name has been used, your Constitution's emergency rule lets any living Voting Member call an Emergency Assembly.
          </p>
        </Panel>
      );
    }
    case "minor_age": {
      const v = getPath("minor_plan.readiness_age") ?? 18;
      return (
        <Panel screen={screen}>
          <Input type="number" min={18} max={35} value={v} onChange={(e) => setPath("minor_plan.readiness_age", Number(e.target.value) || 18)} className="text-lg h-14 w-40" />
        </Panel>
      );
    }
    case "minor_readiness": {
      const v: string[] = getPath("minor_plan.readiness_marks") || ["path_complete", "board_confirms"];
      return (
        <Panel screen={screen}>
          <MultiSelect
            value={v}
            onChange={(next) => setPath("minor_plan.readiness_marks", next)}
            options={[
              { value: "path_complete", label: "Finishes the Successor's Path" },
              { value: "junior_year", label: "Serves one full year in a junior role" },
              { value: "board_confirms", label: "The Board says they are ready, by vote" },
            ]}
          />
        </Panel>
      );
    }
    case "np_path": {
      const v = getPath("non_participating.path") || "";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("non_participating.path", val)} className="space-y-3">
            {[
              { value: "estate_plan", label: "Through my own estate plan" },
              { value: "trust_buyout", label: "I want to look into the trust buyout path with a licensed helper" },
              { value: "both", label: "Both" },
              { value: "na", label: "Does not apply to my family" },
            ].map((o) => (
              <OptionRow key={o.value} name={`np-${o.value}`} value={o.value} label={o.label} groupValue={v} />
            ))}
          </RadioGroup>
        </Panel>
      );
    }
    case "np_note": {
      const v = getPath("non_participating.note") || "";
      return (
        <Panel screen={screen}>
          <Textarea rows={5} value={v} placeholder="Your wish, in your own words. Please do not include passwords, PINs, or account numbers." onChange={(e) => setPath("non_participating.note", e.target.value)} />
        </Panel>
      );
    }
    case "protector_choice": {
      const v = getPath("protector.enabled") || "";
      const name = getPath("protector.name") || "";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("protector.enabled", val)} className="space-y-3">
            <OptionRow name="prot-yes" value="yes" label="Yes, create a Steward Protector role" groupValue={v} />
            <OptionRow name="prot-no" value="no" label="No, not right now" groupValue={v} />
          </RadioGroup>
          {v === "yes" && (
            <>
              <Input className="mt-3" placeholder="Name of the Steward Protector" value={name} onChange={(e) => setPath("protector.name", e.target.value)} />
              <div className="mt-4 rounded-md border border-primary/30 bg-primary/5 p-4 space-y-2">
                <p className="text-sm">
                  The Steward Protector role is set up in your Offices tool with a ready charter that carries all the small, set powers above.
                </p>
                <Button variant="outline" size="sm" onClick={() => navigate("/governance/offices?charter=steward_protector")}>
                  Open the Office Builder with this charter
                </Button>
              </div>
            </>
          )}
        </Panel>
      );
    }
    case "notif_when": {
      const v = getPath("notifications.when") || "";
      const note = getPath("notifications.when_note") || "";
      return (
        <Panel screen={screen}>
          <RadioGroup value={v} onValueChange={(val) => setPath("notifications.when", val)} className="space-y-3">
            <OptionRow name="nw-now" value="now" label="Now, out in the open (this is what we suggest, since they are already walking the path)" groupValue={v} />
            <OptionRow name="nw-trigger" value="at_trigger" label="At the moment the plan turns on" groupValue={v} />
            <OptionRow name="nw-mixed" value="mixed" label="Some now, some later" groupValue={v} />
          </RadioGroup>
          {v === "mixed" && (
            <Textarea className="mt-3" placeholder="Who now, who later" value={note} onChange={(e) => setPath("notifications.when_note", e.target.value)} />
          )}
        </Panel>
      );
    }
    case "notif_who": {
      const v: string[] = getPath("notifications.who") || [];
      const other = getPath("notifications.who_other") || "";
      return (
        <Panel screen={screen}>
          <MultiSelect
            value={v}
            onChange={(next) => setPath("notifications.who", next)}
            options={[
              { value: "registered_agent", label: "Registered agent (the person the state uses to send you official mail)" },
              { value: "insurance", label: "Insurance companies" },
              { value: "vendors", label: "Key vendors and workers" },
              { value: "professionals", label: "Your licensed helpers" },
              { value: "other", label: "Other" },
            ]}
          />
          {v.includes("other") && (
            <Input className="mt-3" placeholder="Who else?" value={other} onChange={(e) => setPath("notifications.who_other", e.target.value)} />
          )}
        </Panel>
      );
    }
    case "asset_digital": {
      const seed = !!getPath("asset_rules.digital.seed_offline_confirmed");
      const drill = !!getPath("asset_rules.digital.annual_drill_confirmed");
      return (
        <Panel screen={screen}>
          <div className="space-y-3">
            <ConfirmRow checked={seed} label="We write down our seed word locations somewhere other than this portal." onChange={(c) => setPath("asset_rules.digital.seed_offline_confirmed", c)} />
            <ConfirmRow checked={drill} label="We will walk through one login drill each year." onChange={(c) => setPath("asset_rules.digital.annual_drill_confirmed", c)} />
          </div>
        </Panel>
      );
    }
    case "asset_business": {
      const v = getPath("asset_rules.business.prepare_acceptance");
      const checked = v === undefined ? true : !!v;
      return (
        <Panel screen={screen}>
          <ConfirmRow checked={checked} label="Get the paper ready in the Documents stage that lets your next manager step in." onChange={(c) => setPath("asset_rules.business.prepare_acceptance", c)} />
        </Panel>
      );
    }
    case "asset_real": {
      const v = !!getPath("asset_rules.real.confirmed");
      return (
        <Panel screen={screen}>
          <ConfirmRow checked={v} label="I get it: my insurance company and any property manager join the tell list." onChange={(c) => setPath("asset_rules.real.confirmed", c)} />
        </Panel>
      );
    }
    case "asset_brokerage": {
      const v = !!getPath("asset_rules.brokerage.confirmed");
      return (
        <Panel screen={screen}>
          <ConfirmRow checked={v} label="I get it: each investment company joins my list in the Execution stage." onChange={(c) => setPath("asset_rules.brokerage.confirmed", c)} />
        </Panel>
      );
    }
    case "asset_trust": {
      const v = !!getPath("asset_rules.trust.confirmed");
      return (
        <Panel screen={screen}>
          <ConfirmRow checked={v} label="I get it: my trust has its own succession rules, and I will look at both together with a licensed helper." onChange={(c) => setPath("asset_rules.trust.confirmed", c)} />
        </Panel>
      );
    }
  }

  return <Panel screen={screen}>{null}</Panel>;
}

function Panel({ screen, children }: { screen: Screen; children: React.ReactNode }) {
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <h2 className="text-3xl md:text-4xl font-serif leading-tight">{screen.title}</h2>
        {screen.helper && <p className="text-base text-muted-foreground">{screen.helper}</p>}
        {screen.subtitle && <p className="text-base text-muted-foreground whitespace-pre-line">{screen.subtitle}</p>}
      </div>
      {children}
    </div>
  );
}

function OptionRow({ name, value, label, groupValue }: { name: string; value: string; label: string; groupValue: string }) {
  return (
    <label className="flex items-center space-x-3 border rounded-md p-4 cursor-pointer hover:bg-accent">
      <RadioGroupItem value={value} id={name} />
      <span className="text-base">{label}</span>
    </label>
  );
}

function ConfirmRow({ checked, label, onChange }: { checked: boolean; label: string; onChange: (c: boolean) => void }) {
  return (
    <label className="flex items-start gap-3 border rounded-md p-4 cursor-pointer hover:bg-accent">
      <Checkbox checked={checked} onCheckedChange={(c) => onChange(!!c)} className="mt-1" />
      <span className="text-base">{label}</span>
    </label>
  );
}

function summarizePrepared(d: Decisions, flags: BranchFlags): string[] {
  const out: string[] = [];
  out.push("Continuity resolution reflecting your order of succession");
  out.push("Incapacity determination rule, written for the Record");
  if (flags.minor_successor) out.push("Bridge successor charter and readiness resolution");
  if (d?.protector?.enabled === "yes") out.push("Steward Protector charter and acceptance");
  if (flags.has_operating_business && d?.asset_rules?.business?.prepare_acceptance !== false)
    out.push("Successor manager acceptance for your operating business");
  out.push("Institution notification register (built in the Execution stage)");
  return out;
}
